import React from 'react';
import styled from '@emotion/styled';

const PresetsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const PresetButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: ${props => props.active ? '#f3f4f6' : 'white'};
  color: ${props => props.active ? '#374151' : '#6b7280'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.active ? '#e5e7eb' : '#f9fafb'};
    border-color: ${props => props.active ? '#9ca3af' : '#9ca3af'};
  }
`;

/**
 * Decoupled Results Filter Presets Component
 * 
 * This component works with any data structure by using a data adapter pattern.
 * It can be used for both preview results and database results.
 * 
 * @param {Object} props
 * @param {Array} props.data - The field data to analyze (any structure)
 * @param {Function} props.dataAdapter - Function to extract field data from the input structure
 * @param {string} props.activeFilter - Currently active filter
 * @param {Function} props.onFilterChange - Callback when filter changes
 * @param {Array} props.customPresets - Optional custom presets to add
 */
const ResultsFilterPresets = ({ 
  data, 
  dataAdapter = defaultDataAdapter, 
  activeFilter, 
  onFilterChange,
  customPresets = []
}) => {
  
  // Extract field data using the provided adapter
  const fieldData = dataAdapter(data);
  
  // Calculate counts for each filter type
  const counts = calculateFilterCounts(fieldData);
  
  // Create standard presets
  const standardPresets = [
    { key: 'all', label: 'All Fields', count: counts.total },
    { key: 'perfect', label: 'Perfect Match', count: counts.perfect },
    { key: 'maybe', label: 'Maybe Match', count: counts.maybe },
    { key: 'mismatch', label: 'Mismatch', count: counts.mismatch },
    { key: 'hasData', label: 'Has Data', count: counts.hasData },
    { key: 'hasCoreData', label: 'Has Core Data', count: counts.hasCoreData },
    { key: 'noCoreData', label: 'No Core Data', count: counts.noCoreData },
    { key: 'overridden', label: 'Overridden', count: counts.overridden }
  ];
  
  // Add review presets only if they have items
  if (counts.clientReview > 0) {
    standardPresets.push({ key: 'clientReview', label: 'Client Review', count: counts.clientReview });
  }
  
  if (counts.leadReview > 0) {
    standardPresets.push({ key: 'leadReview', label: 'Lead Review', count: counts.leadReview });
  }
  
  // Combine standard and custom presets
  const allPresets = [...standardPresets, ...customPresets];
  
  return (
    <PresetsContainer>
      {allPresets.map((preset) => (
        <PresetButton
          key={preset.key}
          active={activeFilter === preset.key}
          onClick={() => onFilterChange(preset.key)}
        >
          {preset.label}{preset.count !== undefined ? ` (${preset.count})` : ''}
        </PresetButton>
      ))}
    </PresetsContainer>
  );
};

/**
 * Default data adapter - extracts field data from common structures
 * Handles both preview results and database results
 */
const defaultDataAdapter = (data) => {
  if (!data) return [];
  
  // Handle different data structures
  if (data.extraction_result) {
    // Preview results structure: { extraction_result: {...}, match_result: {...} }
    const fieldData = data.match_result || data.extraction_result;
    return Object.entries(fieldData).filter(([key]) => key !== '_metadata');
  }
  
  if (data.match_result) {
    // Direct match_result structure
    return Object.entries(data.match_result).filter(([key]) => key !== '_metadata');
  }
  
  if (data.extraction_result) {
    // Direct extraction_result structure  
    return Object.entries(data.extraction_result).filter(([key]) => key !== '_metadata');
  }
  
  // Assume it's already field data: { fieldName: { value, master_value, ... } }
  return Object.entries(data).filter(([key]) => key !== '_metadata');
};

/**
 * Calculate filter counts from field data
 */
const calculateFilterCounts = (fieldData) => {
  const total = fieldData.length;
  
  const perfect = fieldData.filter(([, data]) => {
    if (!data) return false;
    const status = data.system_document_match || data.match_status_override || 'NO_MATCH';
    return status === 'HIGH' || status === 'PERFECT_MATCH';
  }).length;
  
  const maybe = fieldData.filter(([, data]) => {
    if (!data) return false;
    const status = data.system_document_match || data.match_status_override || 'NO_MATCH';
    return status === 'MEDIUM' || status === 'LOW' || status === 'MAYBE_MATCH';
  }).length;
  
  const mismatch = fieldData.filter(([, data]) => {
    if (!data) return false;
    const status = data.system_document_match || data.match_status_override || 'NO_MATCH';
    return status === 'NO_MATCH';
  }).length;
  
  const hasData = fieldData.filter(([, data]) => 
    data && data.value && data.value !== 'Not Found' && data.value.trim() !== ''
  ).length;
  
  const hasCoreData = fieldData.filter(([, data]) => 
    data && data.master_value && data.master_value !== 'Not Found' && data.master_value.trim() !== ''
  ).length;
  
  const noCoreData = fieldData.filter(([, data]) => 
    !data || !data.master_value || data.master_value === 'Not Found' || data.master_value.trim() === ''
  ).length;
  
  const clientReview = fieldData.filter(([, data]) => 
    data && data.flag_override === 'client_review'
  ).length;
  
  const leadReview = fieldData.filter(([, data]) => 
    data && data.flag_override === 'lead_review'
  ).length;
  
  const overridden = fieldData.filter(([, data]) => 
    data && data.overridden === true
  ).length;
  
  return {
    total,
    perfect,
    maybe,
    mismatch,
    hasData,
    hasCoreData,
    noCoreData,
    clientReview,
    leadReview,
    overridden
  };
};

export default ResultsFilterPresets;
